import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { useSelector } from 'react-redux';
import { getIcon4SelectOption } from '@src/components/common/DrillDownToEventScope';
import { DefaultOptionType } from 'antd/es/select';
import { dimensionsCategory } from '@src/utils/dimension/dimensionCategories';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { useAtomValue } from 'jotai';
import { aiCohortDrawerAtom } from '@src/components/ai-alerts/HeaderPanel/SensitivityControl/atoms';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { getAppFlowUsecaseKeyFromFlowId } from '@src/utils/preset';
import { getECODimensionList } from '@src/utils/dimension/getDimensionsForPreset';
import { EXCLUDE_DIMENSION_CATEGORIES_MAP } from '@src/components/ai-alerts/constants';
import { SelectGroupLabel, StyledSelect } from '../styled';
import useDimensionsStore from '@globalStore/dimensionsStore';

const dimensionListCategory = dimensionsCategory[CURRENT_PRODUCT_MODE];

const DimensionSelector = ({
  dimension,
  disabled = false,
  availableDimensions = [],
  hiddenDimensions = [],
  onChange,
}: {
  dimension: string;
  disabled?: boolean;
  availableDimensions?: any[];
  hiddenDimensions?: string[];
  onChange: (newDimension: string) => void;
}) => {
  const { dimensionsMeta } = useDimensionsStore.getState();
  const aiCohortConfig = useAtomValue(aiCohortDrawerAtom);

  const availableMap = useMemo(() => {
    const map: Record<string, any> = {};
    availableDimensions.forEach((d) => {
      if (d.field) map[d.field] = d;
    });
    return map;
  }, [availableDimensions]);

  // Compute filtered dimensions based on flow, metric, exclusions, and availableDimensions
  const dimensions = useMemo(() => {
    let baseDimensions = dimensionsMeta;
    const currentMetric = aiCohortConfig?.metrics?.[0];
    if (!currentMetric) return baseDimensions;
    if (aiCohortConfig?.isFlow) {
      const flowId = getMetricMetaByKey({ metricKey: currentMetric })?.flowID;
      const usecaseKey = getAppFlowUsecaseKeyFromFlowId(flowId);
      baseDimensions = getECODimensionList({ usecaseKey, widgetMeta: { flowId } }, baseDimensions);
    }
    const excludedCategories = EXCLUDE_DIMENSION_CATEGORIES_MAP.getExclusions(currentMetric);
    return (
      baseDimensions?.filter((d) => {
        if (!d.category) return true;
        const isExcluded = excludedCategories.includes(d.category.toLowerCase());
        const isHidden = hiddenDimensions?.includes(d.groupBy as string);
        const isAvailable = availableDimensions?.length > 0 ? !!availableMap[d.field] : false;
        return !isExcluded && !isHidden && isAvailable;
      }) || []
    );
  }, [dimensionsMeta, aiCohortConfig, availableMap, hiddenDimensions, availableDimensions]);

  // Build grouped options for the dropdown
  const options = useMemo(() => {
    const filtered = dimensions.filter((d) => d.field && availableMap[d.field]);
    const createDimensionOption = (d: any, idx: number): DefaultOptionType => {
      const ad = availableMap[d.field];
      const dimensionText = ad?.value || d.value || d.name;
      const optionLabel = d.isHighCardinality ? (
        <Tooltip
          title="High Cardinality Dimension"
          zIndex={99999}
          getPopupContainer={() => document.body}
          overlayStyle={{ zIndex: 99999 }}
        >
          {dimensionText}
        </Tooltip>
      ) : (
        dimensionText
      );
      return {
        key: `${ad?.backendName || d.backendName || d.field || dimensionText}__${d.category || ''}__${idx}`,
        value: dimensionText,
        label: optionLabel,
        searchText: dimensionText,
        backendName: ad?.backendName,
        disabled: d.isHighCardinality,
      };
    };
    // Group by category
    const dimensionByCategory: Record<string, any[]> = {};
    filtered.forEach((d) => {
      const category = d.category || 'Other';
      if (!dimensionByCategory[category]) dimensionByCategory[category] = [];
      dimensionByCategory[category].push(d);
    });
    const categoryKeys = Object.keys(dimensionByCategory);
    if (categoryKeys.length > 0) {
      return categoryKeys.map((category) => ({
        label: (
          <SelectGroupLabel>
            {getIcon4SelectOption(category)}
            {dimensionListCategory[category] || category}
          </SelectGroupLabel>
        ),
        value: category,
        options: dimensionByCategory[category]
          .sort((a, b) =>
            (availableMap[a.field]?.value || a.name).localeCompare(
              availableMap[b.field]?.value || b.name,
            ),
          )
          .map((d, idx) => createDimensionOption(d, idx)),
      }));
    }
    // Fallback: flat list if no categories
    return filtered
      .sort((a, b) =>
        (availableMap[a.field]?.value || a.name).localeCompare(
          availableMap[b.field]?.value || b.name,
        ),
      )
      .map((d, idx) => createDimensionOption(d, idx));
  }, [dimensions, availableMap]);

  return (
    <StyledSelect
      showSearch
      options={options}
      optionFilterProp="searchText"
      value={dimension}
      onChange={(val) => onChange(val as string)}
      style={{ fontSize: '18px' }}
      disabled={disabled}
      styles={{ popup: { root: { minWidth: 280 } } }}
      getPopupContainer={() => document.body}
      allowClear
      virtual={false}
      filterOption={(input, option) => {
        if (!option?.searchText) return false;
        return option.searchText.toLowerCase().includes(input.toLowerCase());
      }}
    />
  );
};

export default DimensionSelector;
